/**
 * payablesAging.ts — Pure computation of supplier outstanding (payables) aging:
 * the mirror image of the receivables khata. Who the shop owes, how much, and
 * how long the bills have been unpaid, bucketed 0–30 / 31–60 / 61–90 / 90+.
 *
 * Why this is harder than receivables: an invoice carries its own `due_amount`,
 * but a purchase order does not track a per-bill outstanding. What the app keeps
 * is `suppliers.current_balance` (a single, ageless total) and a
 * `supplier_ledger` of dated debits and credits. So the aging is reconstructed
 * by **FIFO**: bills are the debits (`INVOICE`, `PURCHASE`), payments and
 * returns are the credits (`PAYMENT`, `RETURN`, `PURCHASE_RETURN`); credits are
 * applied to the oldest bills first, and whatever bill amount remains unpaid is
 * aged by that bill's date.
 *
 * Rules:
 *  1. **`current_balance` is the authoritative total.** It is what the rest of
 *     the app shows, so each supplier's buckets are made to sum to it. The FIFO
 *     pass explains the *age*; any gap between the ledger and the balance (an
 *     imported opening balance with no ledger row, or the ≥0 clamp on an
 *     overpaid supplier) is reconciled and disclosed as `unaged`, never hidden.
 *  2. **Aged by bill date; overdue is the past-term subset.** A bill is overdue
 *     when its date + the supplier's `credit_days` has passed.
 *  3. **Over credit limit** is flagged against `credit_limit`, like receivables.
 *
 * `asOf` is injected for deterministic, testable buckets. See
 * `payablesDataSource.ts` for the DB bridge.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const BUCKET_LABELS = ['0–30', '31–60', '61–90', '90+'] as const;
const BUCKET_BOUNDS = [30, 60, 90];

/** Ledger types that increase what the shop owes (bills). */
const DEBIT_TYPES = new Set(['INVOICE', 'PURCHASE']);
/** Ledger types that decrease it (payments, returns). */
const CREDIT_TYPES = new Set(['PAYMENT', 'RETURN', 'PURCHASE_RETURN']);

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function ageInDays(from: Date, asOf: Date): number {
  return Math.max(0, Math.floor((asOf.getTime() - from.getTime()) / DAY_MS));
}

export function bucketIndex(days: number): number {
  for (let i = 0; i < BUCKET_BOUNDS.length; i++) {
    if (days <= BUCKET_BOUNDS[i]) return i;
  }
  return BUCKET_BOUNDS.length;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface LedgerEntryInput {
  supplierId: string;
  /** INVOICE/PURCHASE = debit; PAYMENT/RETURN/PURCHASE_RETURN = credit. */
  type: string;
  /** Positive magnitude; direction comes from `type`. */
  amount: number;
  date: Date;
}

export interface PayableSupplierInput {
  id: string;
  name: string | null;
  phone: string | null;
  creditDays: number;
  creditLimit: number;
  /** Authoritative total owed (clamped ≥ 0 by the app). */
  currentBalance: number;
}

export interface PayablesAgingInput {
  ledger: LedgerEntryInput[];
  suppliers: PayableSupplierInput[];
  asOf: Date;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface SupplierPayable {
  supplierId: string;
  name: string;
  phone: string | null;
  /** [0–30, 31–60, 61–90, 90+] outstanding amounts, summing to `total`. */
  buckets: [number, number, number, number];
  total: number;
  /** Portion of a bill past its date + credit_days. */
  overdueAmount: number;
  /** Balance the FIFO pass could not date (opening balance / clamp); in 90+. */
  unaged: number;
  creditDays: number;
  creditLimit: number;
  overLimit: boolean;
  /** Age of the oldest unpaid bill, in days (0 when only `unaged`). */
  oldestDays: number;
}

export interface PayablesAging {
  rows: SupplierPayable[];
  totals: {
    buckets: [number, number, number, number];
    total: number;
    overdueAmount: number;
  };
  supplierCount: number;
  overdueCount: number;
  overLimitCount: number;
  /** Total balance that could not be aged from the ledger — a data-quality note. */
  unagedTotal: number;
}

interface Bill {
  amount: number;
  remaining: number;
  date: Date;
}

function ageOneSupplier(
  supplier: PayableSupplierInput,
  entries: LedgerEntryInput[],
  asOf: Date,
): SupplierPayable {
  const bills: Bill[] = entries
    .filter((e) => DEBIT_TYPES.has(e.type))
    .map((e) => ({ amount: e.amount, remaining: e.amount, date: e.date }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  let credit = entries
    .filter((e) => CREDIT_TYPES.has(e.type))
    .reduce((s, e) => s + e.amount, 0);

  // Apply credits to the oldest bills first (FIFO).
  for (const bill of bills) {
    if (credit <= 0) break;
    const applied = Math.min(credit, bill.remaining);
    bill.remaining -= applied;
    credit -= applied;
  }

  const buckets: [number, number, number, number] = [0, 0, 0, 0];
  let overdue = 0;
  let oldestDays = 0;
  const dueMs = supplier.creditDays * DAY_MS;
  for (const bill of bills) {
    if (bill.remaining <= 0) continue;
    const days = ageInDays(bill.date, asOf);
    buckets[bucketIndex(days)] += bill.remaining;
    if (bill.date.getTime() + dueMs < asOf.getTime()) overdue += bill.remaining;
    oldestDays = Math.max(oldestDays, days);
  }

  const ledgerOut = buckets[0] + buckets[1] + buckets[2] + buckets[3];
  const balance = supplier.currentBalance;

  // Reconcile the ledger-explained outstanding to the authoritative balance.
  let unaged = 0;
  const gap = balance - ledgerOut;
  if (gap > 0.01) {
    // Balance exceeds what the ledger explains — an opening balance or untracked
    // debit. Treat as aged (oldest) so it prompts review; keep it out of overdue.
    buckets[3] += gap;
    unaged = gap;
  } else if (gap < -0.01 && ledgerOut > 0) {
    // Ledger explains more than the (clamped) balance — scale the buckets down
    // proportionally so the row still totals the authoritative balance.
    const scale = balance / ledgerOut;
    for (let i = 0; i < 4; i++) buckets[i] *= scale;
    overdue *= scale;
  }

  const rounded = buckets.map(round2) as [number, number, number, number];
  return {
    supplierId: supplier.id,
    name: supplier.name?.trim() || 'Unknown supplier',
    phone: supplier.phone,
    buckets: rounded,
    total: round2(rounded[0] + rounded[1] + rounded[2] + rounded[3]),
    overdueAmount: round2(overdue),
    unaged: round2(unaged),
    creditDays: supplier.creditDays,
    creditLimit: supplier.creditLimit,
    overLimit: supplier.creditLimit > 0 && balance > supplier.creditLimit,
    oldestDays: unaged > 0 ? Math.max(oldestDays, 91) : oldestDays,
  };
}

export function buildPayablesAging(input: PayablesAgingInput): PayablesAging {
  const { ledger, suppliers, asOf } = input;

  const bySupplier = new Map<string, LedgerEntryInput[]>();
  for (const e of ledger) {
    const arr = bySupplier.get(e.supplierId);
    if (arr) arr.push(e);
    else bySupplier.set(e.supplierId, [e]);
  }

  const rows = suppliers
    .filter((s) => s.currentBalance > 0.01)
    .map((s) => ageOneSupplier(s, bySupplier.get(s.id) ?? [], asOf))
    .sort((a, b) => b.total - a.total);

  const totals = rows.reduce(
    (t, r) => {
      t.buckets[0] += r.buckets[0];
      t.buckets[1] += r.buckets[1];
      t.buckets[2] += r.buckets[2];
      t.buckets[3] += r.buckets[3];
      t.overdueAmount += r.overdueAmount;
      t.unaged += r.unaged;
      return t;
    },
    { buckets: [0, 0, 0, 0] as [number, number, number, number], overdueAmount: 0, unaged: 0 },
  );
  const tb = totals.buckets.map(round2) as [number, number, number, number];

  return {
    rows,
    totals: {
      buckets: tb,
      total: round2(tb[0] + tb[1] + tb[2] + tb[3]),
      overdueAmount: round2(totals.overdueAmount),
    },
    supplierCount: rows.length,
    overdueCount: rows.filter((r) => r.overdueAmount > 0).length,
    overLimitCount: rows.filter((r) => r.overLimit).length,
    unagedTotal: round2(totals.unaged),
  };
}
